import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Phone, Coins } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SearchBar from "../components/SearchBar";
import PropertyCard from "../components/PropertyCard";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";

const DEFAULT_HERO = "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=2000&q=85";

const POPULAR = [
  { city: "Alger", img: "https://images.unsplash.com/photo-1723103639391-f4a06d660b20?w=800&q=85" },
  { city: "Oran", img: "https://images.unsplash.com/photo-1766854268133-7ac973e1ec8a?w=800&q=85" },
  { city: "Constantine", img: "https://images.unsplash.com/photo-1772475385317-09f9ef320474?w=800&q=85" },
  { city: "Annaba", img: "https://images.unsplash.com/photo-1773397357324-005ae0145c8c?w=800&q=85" },
];

export default function Home() {
  const { t } = useLang();
  const [featured, setFeatured] = useState([]);
  const [recent, setRecent] = useState([]);
  const [heroImg, setHeroImg] = useState(DEFAULT_HERO);

  useEffect(() => {
    api.get("/properties/featured").then((r) => setFeatured(r.data.items)).catch(() => {});
    api.get("/properties", { params: { limit: 8 } }).then((r) => setRecent(r.data.items)).catch(() => {});
    api.get("/config").then((r) => { if (r.data?.hero_image_url) setHeroImg(r.data.hero_image_url); }).catch(() => {});
  }, []);

  return (
    <div>
      <Header />

      {/* HERO */}
      <section className="relative pt-24 px-4 md:px-10">
        <div className="relative w-full h-[80vh] min-h-[600px] max-h-[780px] rounded-3xl overflow-hidden">
          <img src={heroImg} alt="hero" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />

          <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6">
            <span className="text-xs uppercase tracking-[0.3em] text-white/80 font-semibold fade-up">
              {t("tagline")}
            </span>
            <h1 className="text-white text-4xl md:text-6xl lg:text-7xl font-medium mt-5 max-w-4xl leading-[1.05] tracking-tight fade-up delay-1">
              {t("heroTitle")}
            </h1>
            <p className="text-white/85 text-base md:text-lg mt-5 max-w-2xl fade-up delay-2">
              {t("valueProp")}
            </p>

            <div className="w-full max-w-5xl mt-10 fade-up delay-3">
              <SearchBar big />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <span className="text-xs uppercase tracking-[0.3em] text-[#1B8F5A] font-bold">
                {t("featured")}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mt-3 text-[#111827]">
                {t("featuredSub")}
              </h2>
            </div>
            <Link
              to="/search"
              className="text-sm font-medium text-[#1B8F5A] inline-flex items-center gap-1 hover:gap-2 transition-all"
              data-testid="featured-view-all"
            >
              {t("viewAll")} <ArrowRight size={15} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.slice(0, 6).map((p) => (
              <PropertyCard key={p.id} item={p} />
            ))}
          </div>
        </section>
      )}

      {/* TRUST */}
      <section className="bg-[#F4F9F6] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl mb-14">
            <span className="text-xs uppercase tracking-[0.3em] text-[#1B8F5A] font-bold">
              {t("whyUs")}
            </span>
            <h2 className="text-3xl md:text-4xl font-medium mt-3 text-[#111827]">
              {t("whyUs")}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { Icon: ShieldCheck, title: t("trust1Title"), desc: t("trust1Desc") },
              { Icon: Phone, title: t("trust2Title"), desc: t("trust2Desc") },
              { Icon: Coins, title: t("trust3Title"), desc: t("trust3Desc") },
            ].map(({ Icon, title, desc }, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-[#1B8F5A]/8 flex items-center justify-center">
                  <Icon size={22} className="text-[#1B8F5A]" />
                </div>
                <h3 className="text-xl font-semibold mt-5 text-[#111827]">{title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* POPULAR CITIES */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="max-w-2xl mb-12">
          <span className="text-xs uppercase tracking-[0.3em] text-[#1B8F5A] font-bold">
            {t("popularCities")}
          </span>
          <h2 className="text-3xl md:text-4xl font-medium mt-3 text-[#111827]">
            {t("popularTitle")}
          </h2>
          <p className="text-slate-600 mt-3">{t("popularSub")}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {POPULAR.map((p) => (
            <Link
              key={p.city}
              to={`/search?wilaya=${encodeURIComponent(p.city)}`}
              className="group relative aspect-[4/5] rounded-2xl overflow-hidden"
              data-testid={`popular-${p.city.toLowerCase()}`}
            >
              <img src={p.img} alt={p.city} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="text-white text-2xl font-semibold">{p.city}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* RECENT */}
      {recent.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 pb-20 md:pb-28">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <h2 className="text-3xl md:text-4xl font-medium text-[#111827]">
              {t("recentListings")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {recent.slice(0, 8).map((p) => (
              <PropertyCard key={p.id} item={p} />
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
